import React, { useState, useEffect } from 'react';
import './Dashboard.css';
import modulesConfig from '../modules.json';
import Users from './admin/Users';
import Sites from './admin/Sites';
import Permissions from './admin/Permissions';
import ChangePassword from './ChangePassword';
import Maintenance from './Maintenance';

const PAGE_TITLES = {
  home: { icon: '🏠', label: 'Accueil' },
  maintenance: { icon: '🔧', label: 'Maintenance' },
  'admin-users': { icon: '👥', label: 'Utilisateurs' },
  'admin-sites': { icon: '📍', label: 'Sites' },
  'admin-permissions': { icon: '🔐', label: 'Permissions' },
};

const isMobile = () => typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches;

export default function Dashboard({ user, setUser, branding, onLogout }) {
  const [view, setView] = useState('home');
  const [sidebarOpen, setSidebarOpen] = useState(() => !isMobile());
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [dark, setDark] = useState(localStorage.getItem('immo-dark') === 'true');
  const [headerAction, setHeaderAction] = useState(null); // boutons d'action (haut-droite), alimentés par la vue active

  const isAdmin = user.role === 'admin' || user.role === 'super-admin';
  const activeModules = (modulesConfig.modules || []).filter((m) => m.actif);

  // Mobile : fermer le drawer après navigation (jamais sur desktop)
  useEffect(() => {
    if (isMobile()) setSidebarOpen(false);
  }, [view]);

  // Mobile : verrou de scroll quand le drawer est ouvert
  useEffect(() => {
    if (isMobile() && sidebarOpen) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
    document.body.style.overflow = '';
  }, [sidebarOpen]);

  const toggleDark = () => {
    const next = !dark;
    setDark(next);
    localStorage.setItem('immo-dark', String(next));
    document.documentElement.setAttribute('data-theme', next ? 'dark' : 'light');
    setShowUserMenu(false);
  };

  const navItem = (id, icon, label) => (
    <button
      key={id}
      className={`sidebar-item ${view === id ? 'active' : ''}`}
      onClick={() => setView(id)}
    >
      <span className="sidebar-item-icon">{icon}</span>
      <span className="sidebar-item-label">{label}</span>
    </button>
  );

  const renderView = () => {
    switch (view) {
      case 'maintenance': return <Maintenance currentUser={user} setHeaderAction={setHeaderAction} />;
      case 'admin-users': return isAdmin ? <Users currentUser={user} setHeaderAction={setHeaderAction} /> : <Forbidden />;
      case 'admin-sites': return isAdmin ? <Sites currentUser={user} setHeaderAction={setHeaderAction} /> : <Forbidden />;
      case 'admin-permissions': return isAdmin ? <Permissions /> : <Forbidden />;
      default: return <Home user={user} branding={branding} />;
    }
  };

  return (
    <div className={`shell ${sidebarOpen ? 'sidebar-open' : ''}`}>
      {sidebarOpen && <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} aria-hidden="true" />}

      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">{(branding.short_name || 'G').charAt(0)}</div>
          {sidebarOpen && <span className="sidebar-brand">{branding.short_name || 'Gestion'}</span>}
        </div>
        <nav className="sidebar-scroll">
          {navItem('home', '🏠', 'Accueil')}

          {activeModules.length > 0 && (
            <div className="sidebar-section">
              <div className="sidebar-section-title">Modules</div>
              {activeModules.map((m) => navItem(m.id, m.icon, m.label))}
            </div>
          )}

          {isAdmin && (
            <div className="sidebar-section">
              <div className="sidebar-section-title">Administration</div>
              {navItem('admin-users', '👥', 'Utilisateurs')}
              {navItem('admin-sites', '📍', 'Sites')}
              {navItem('admin-permissions', '🔐', 'Permissions')}
            </div>
          )}
        </nav>

        <div className="sidebar-footer">
          <button className="sidebar-user" onClick={() => setShowUserMenu((v) => !v)} title={`${user.prenom || ''} ${user.nom || ''}`.trim() || user.username}>
            <span className="avatar-btn">{(user.prenom || user.username || '?').charAt(0).toUpperCase()}</span>
            <span className="sidebar-user-info">
              <span className="sidebar-user-name">{user.prenom} {user.nom}</span>
              <span className="sidebar-user-role">{user.role}</span>
            </span>
          </button>
          {showUserMenu && (
            <>
              <div className="user-menu-backdrop" onClick={() => setShowUserMenu(false)} />
              <div className="user-menu user-menu--up">
                <div className="user-menu-head">
                  <div className="user-menu-name">{user.prenom} {user.nom}</div>
                  <div className="user-menu-role">{user.role}</div>
                </div>
                <button className="user-menu-item" onClick={() => { setShowPassword(true); setShowUserMenu(false); }}>
                  🔒 Modifier mon mot de passe
                </button>
                <button className="user-menu-item" onClick={toggleDark}>
                  {dark ? '☀️ Mode clair' : '🌙 Mode sombre'}
                </button>
                <div className="user-menu-sep" />
                <button className="user-menu-item danger" onClick={onLogout}>🚪 Se déconnecter</button>
              </div>
            </>
          )}
        </div>
      </aside>

      <header className="header">
        <button className="burger" onClick={() => setSidebarOpen((v) => !v)} aria-label="Menu">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M3 5h14M3 10h14M3 15h14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
        </button>
        <h1 className="header-title" onClick={() => setView('home')}>
          <span className="header-icon">{PAGE_TITLES[view]?.icon || '🏠'}</span>
          <span className="header-title-text">{PAGE_TITLES[view]?.label || 'Accueil'}</span>
        </h1>
        <div className="header-spacer" />
        <div className="header-actions">{headerAction}</div>
      </header>

      <main className="content">{renderView()}</main>

      {showPassword && <ChangePassword onClose={() => setShowPassword(false)} />}
    </div>
  );
}

function Home({ user, branding }) {
  return (
    <div className="page-container">
      <div className="home-hero">
        <h2>Bienvenue, {user.prenom || user.username}.</h2>
        <p>
          Le socle de <strong>{branding.app_title || 'la plateforme'}</strong> est en place :
          authentification, utilisateurs, sites, permissions et design system responsive.
        </p>
        <p className="home-next">Les modules métier seront ajoutés ensuite. La sidebar les affichera automatiquement.</p>
      </div>
    </div>
  );
}

function Forbidden() {
  return <div className="page-container"><div className="table-empty">Accès réservé aux administrateurs.</div></div>;
}
